import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Text,
  Flex,
  HStack,
  VStack,
  Button,
  IconButton,
  Badge,
  Spinner,
  Divider,
  SimpleGrid,
  Image,
  Icon,
  Checkbox,
  Input,
  Select,
  Code,
  Textarea,
  FormControl,
  FormLabel,
  FormHelperText,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  OrderedList,
  ListItem,
  Alert,
  AlertIcon,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon, CheckCircleIcon, EditIcon } from '@chakra-ui/icons';
import { FiDatabase, FiPlay, FiLink, FiClock, FiExternalLink, FiChevronRight, FiFolder } from 'react-icons/fi';
import { connectorIcon } from '../assets/connectors';
import { useQuery } from 'react-query';
import {
  integrationsApi,
  collectionsApi,
  IntegrationType,
  IntegrationField,
  Integration,
  AgentGuide,
} from '../services/api';

const statusColor = (status: string): string => {
  if (status === 'ok' || status === 'connected') return 'green';
  if (status === 'error') return 'red';
  if (status === 'paused') return 'orange';
  return 'gray';
};

// Status em português (sem termos técnicos) para o cartão.
const statusLabel = (status: string): string => ({
  connected: 'Conectado',
  ok: 'Ativo',
  error: 'Erro',
  created: 'Aguardando',
  empty: 'Sem novidades',
  paused: 'Pausado por inatividade',
} as Record<string, string>)[status] || status;

const formatDate = (iso?: string | null): string => {
  if (!iso) return '—';
  const [d, t] = iso.split('T');
  const [y, m, day] = (d || '').split('-');
  return y ? `${day}/${m}/${y} ${(t || '').slice(0, 5)}` : iso;
};

// Ícone do serviço do conector (logo real); cai no ícone genérico se não houver.
const ConnectorIcon = ({ type, size = '22px' }: { type: string; size?: string }) => {
  const src = connectorIcon(type);
  return src
    ? <Image src={src} alt="" boxSize={size} borderRadius="sm" objectFit="contain" flexShrink={0} />
    : <FiDatabase />;
};

// Ícone em "selo" — moldura arredondada e fundo neutro, para que logos de origens
// diferentes (jpg/png/webp com fundos distintos) fiquem visualmente consistentes.
const ConnectorBadge = ({ type, size = '44px' }: { type: string; size?: string }) => (
  <Flex
    align="center"
    justify="center"
    boxSize={size}
    bg="gray.50"
    borderWidth="1px"
    borderColor="gray.100"
    borderRadius="lg"
    flexShrink={0}
  >
    <ConnectorIcon type={type} size="26px" />
  </Flex>
);

// Descrição curta por conector — o ícone é o atalho visual; o nome dá clareza;
// a descrição diz, em uma linha, o que o conector faz (recognition > recall).
const CONNECTOR_DESC: Record<string, string> = {
  whatsapp: 'Capture as mensagens do seu WhatsApp e converse com elas.',
  google_drive: 'Converse com os arquivos do seu Google Drive.',
  google_drive_managed: 'Conecte seu Google Drive com 1 clique e pergunte sobre os arquivos.',
  sharepoint: 'Traga documentos do SharePoint para o chat.',
  sharepoint_managed: 'Conecte o SharePoint com 1 clique e pergunte sobre os documentos.',
  s3: 'Importe arquivos de um bucket Amazon S3.',
  sql: 'Indexe registros de um banco de dados SQL.',
  sql_agent: 'Conecte um banco interno sem abrir portas (agente de saída).',
};
const connectorDesc = (type: string): string =>
  CONNECTOR_DESC[type] || 'Importe dados desta fonte para o seu RAG.';

// Renderiza um campo do schema conforme o tipo declarado pelo backend.
const FieldInput = ({
  field,
  value,
  onChange,
  columns,
}: {
  field: IntegrationField;
  value: any;
  onChange: (v: any) => void;
  columns?: string[];
}) => {
  // Seletor de colunas (após Testar descobrir as colunas da tabela).
  if (field.column_picker && columns && columns.length > 0) {
    if (field.multi) {
      const selectedCols = String(value ?? '').split(',').map((s) => s.trim()).filter(Boolean);
      const toggle = (c: string) => {
        const next = selectedCols.includes(c) ? selectedCols.filter((x) => x !== c) : [...selectedCols, c];
        onChange(next.join(','));
      };
      return (
        <Box maxH="140px" overflowY="auto" borderWidth="1px" borderRadius="md" p={2}>
          {columns.map((c) => (
            <Checkbox key={c} size="sm" display="block" isChecked={selectedCols.includes(c)} onChange={() => toggle(c)}>
              {c}
            </Checkbox>
          ))}
        </Box>
      );
    }
    return (
      <Select placeholder="Selecione a coluna" value={value ?? ''} onChange={(e) => onChange(e.target.value)} size="sm">
        {columns.map((c) => <option key={c} value={c}>{c}</option>)}
      </Select>
    );
  }
  if (field.type === 'select') {
    return (
      <Select
        placeholder="Selecione"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        size="sm"
      >
        {(field.options || []).map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </Select>
    );
  }
  return (
    <Input
      type={field.type === 'password' ? 'password' : field.type === 'number' ? 'number' : 'text'}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      size="sm"
    />
  );
};

const IntegrationWizard = ({
  types,
  isOpen,
  onClose,
  onSaved,
  editId,
  onAgentCreated,
}: {
  types: IntegrationType[];
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  editId?: string | null;
  onAgentCreated?: (token: string, guide: AgentGuide) => void;
}) => {
  const toast = useToast();
  const isEdit = !!editId;
  const [selected, setSelected] = useState<IntegrationType | null>(null);
  const [name, setName] = useState('');
  const [targetCollection, setTargetCollection] = useState('');
  const [config, setConfig] = useState<Record<string, any>>({});
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [columns, setColumns] = useState<string[]>([]); // colunas descobertas no Testar (SQL)
  const [tables, setTables] = useState<string[]>([]);   // tabelas descobertas no Testar (SQL)
  const [creatingCollection, setCreatingCollection] = useState(false); // base nova vs. selecionar
  const [syncMode, setSyncMode] = useState<string>('periodic');        // regra de sincronização (F1)
  const [syncInterval, setSyncInterval] = useState<number>(1440);      // minutos (1440 = diário)
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);

  // Bases (coleções) existentes para o usuário SELECIONAR o destino (sem digitar errado).
  const { data: collections = [] } = useQuery('collections-for-integration', collectionsApi.list, {
    staleTime: 30000,
  });

  // Modo edição: ao abrir, busca a config atual e pré-preenche o formulário.
  // Segredos voltam em branco (o backend mantém o valor se ficarem assim).
  useEffect(() => {
    if (!isOpen || !editId) return;
    let active = true;
    setLoadingEdit(true);
    (async () => {
      try {
        const data = await integrationsApi.getConfig(editId);
        if (!active) return;
        setSelected(types.find((t) => t.type === data.type) || null);
        setName(data.name);
        setTargetCollection(data.target_collection);
        setConfig(data.config || {});
        setSyncMode((data.config && data.config.sync_mode) || 'periodic');
        setSyncInterval(Number((data.config && data.config.sync_interval_minutes) || 1440));
        setTestResult(null);
        setColumns([]);
      } catch {
        if (active) toast({ title: 'Não foi possível carregar a integração', status: 'error', duration: 3000, isClosable: true });
      } finally {
        if (active) setLoadingEdit(false);
      }
    })();
    return () => { active = false; };
  }, [isOpen, editId, types, toast]);

  const reset = () => {
    setSelected(null);
    setName('');
    setTargetCollection('');
    setConfig({});
    setTestResult(null);
    setColumns([]);
    setTables([]);
    setCreatingCollection(false);
    setSyncMode('periodic');
    setSyncInterval(1440);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const setField = (key: string, value: any) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
    // Só invalida o teste quando muda um campo de CONEXÃO. Selecionar tabela/colunas
    // (que vieram justamente do teste) não deve exigir testar de novo nem bloquear o salvar.
    const f = selected?.fields.find((x) => x.name === key);
    if (!f?.table_picker && !f?.column_picker) {
      setTestResult(null);
      setTables([]);
      setColumns([]);
    }
  };

  // Campos só de conexão (exclui os que dependem do Testar): habilitam o botão Testar.
  const missingForTest = (): boolean => {
    if (!selected) return true;
    return selected.fields.some(
      (f) => f.required && !f.table_picker && !f.column_picker && !String(config[f.name] ?? '').trim(),
    );
  };

  const missingRequired = (): boolean => {
    if (!selected) return true;
    if (!name.trim()) return true;
    if (!isEdit && !targetCollection.trim()) return true;
    // Na edição, segredos em branco são preservados — não bloqueiam o salvar.
    return selected.fields.some(
      (f) => f.required && !(isEdit && f.secret) && !String(config[f.name] ?? '').trim(),
    );
  };

  // cfgOverride permite testar com um valor recém-escolhido (ex.: a tabela) antes
  // do setState propagar — usado para descobrir as colunas ao selecionar a tabela.
  const runTest = async (cfgOverride?: Record<string, any>) => {
    if (!selected) return;
    const cfg = cfgOverride || config;
    setTesting(true);
    setTestResult(null);
    try {
      const res = await integrationsApi.test(selected.type, cfg);
      setTestResult(res);
      if (res.tables) setTables(res.tables);
      setColumns(res.columns && res.columns.length ? res.columns : []);
    } catch (e: any) {
      setTestResult({ ok: false, message: e?.response?.data?.detail || 'Falha ao testar' });
    } finally {
      setTesting(false);
    }
  };

  // Ao escolher a tabela, já testa de novo para descobrir as colunas dela.
  const onTablePick = (key: string, value: string) => {
    const next = { ...config, [key]: value };
    setConfig(next);
    setColumns([]);
    setTestResult(null);
    if (value) runTest(next);
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      // Regra de sincronização (só para conectores que não são tempo-real/push).
      const syncFields = selected.mode === 'agent'
        ? {}
        : { sync_mode: syncMode, sync_interval_minutes: syncMode === 'periodic' ? syncInterval : undefined };
      if (isEdit && editId) {
        await integrationsApi.update(editId, { name: name.trim(), config, ...syncFields });
        toast({ title: 'Integração atualizada', status: 'success', duration: 2500, isClosable: true });
      } else {
        const res = await integrationsApi.create({
          type: selected.type,
          name: name.trim(),
          target_collection: targetCollection.trim(),
          config,
          ...syncFields,
        });
        toast({ title: 'Integração salva', status: 'success', duration: 2500, isClosable: true });
        // Agente Local: abre o guia com o token recém-gerado (mostrado uma vez).
        if (res.agent_token && res.agent_guide && onAgentCreated) {
          onAgentCreated(res.agent_token, res.agent_guide);
        }
      }
      onSaved();
      handleClose();
    } catch (e: any) {
      toast({
        title: isEdit ? 'Erro ao atualizar' : 'Erro ao salvar',
        description: e?.response?.data?.detail || 'Tente novamente',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {selected
            ? `${isEdit ? 'Editar' : 'Nova'} integração — ${selected.label}`
            : isEdit ? 'Editar integração' : 'Nova integração'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {/* Carregando a config na edição */}
          {isEdit && loadingEdit && (
            <Flex justify="center" py={10}>
              <Spinner color="brand.500" />
            </Flex>
          )}

          {/* Passo 1: escolher o tipo de conector (só na criação) */}
          {!selected && !isEdit && (
            <VStack align="stretch" spacing={3}>
              <Text color="gray.600" fontSize="sm">
                Escolha de onde os dados serão importados:
              </Text>
              {types.map((t) => (
                <Box
                  key={t.type}
                  borderWidth="1px"
                  borderRadius="lg"
                  p={4}
                  cursor="pointer"
                  transition="all 0.15s"
                  _hover={{ borderColor: 'brand.300', bg: 'brand.50', shadow: 'sm' }}
                  onClick={() => setSelected(t)}
                >
                  <HStack spacing={4} align="center">
                    <ConnectorBadge type={t.type} />
                    <VStack align="flex-start" spacing={0.5} flex={1} minW={0}>
                      <Text fontWeight="semibold" noOfLines={1}>{t.label}</Text>
                      <Text fontSize="sm" color="gray.500" noOfLines={2}>
                        {connectorDesc(t.type)}
                      </Text>
                    </VStack>
                    <Icon as={FiChevronRight} color="gray.400" flexShrink={0} />
                  </HStack>
                </Box>
              ))}
            </VStack>
          )}

          {/* Passo 2: instruções + formulário gerado pelo schema */}
          {selected && (
            <VStack align="stretch" spacing={4}>
              {selected.instructions?.length > 0 && (
                <Box bg="gray.50" borderRadius="md" p={3}>
                  <Text fontSize="sm" fontWeight="medium" mb={1}>
                    Passo a passo
                  </Text>
                  <OrderedList fontSize="sm" color="gray.600" spacing={1}>
                    {selected.instructions.map((step, i) => (
                      <ListItem key={i}>{step}</ListItem>
                    ))}
                  </OrderedList>
                </Box>
              )}

              {/* Link direto para o console (Google/Azure) — o leigo não sabe onde achar Client ID/Secret. */}
              {selected.doc_url && (
                <Button
                  as="a"
                  href={selected.doc_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="sm"
                  colorScheme="blue"
                  variant="outline"
                  leftIcon={<FiExternalLink />}
                  alignSelf="flex-start"
                >
                  {selected.doc_label || 'Onde encontro isso? Abrir o guia'}
                </Button>
              )}

              {/* Valor que o usuário precisa colar no console (ex.: URI de redirecionamento). */}
              {selected.copy_value && (
                <Box>
                  <Text fontSize="xs" fontWeight="medium" mb={1}>{selected.copy_value.label}</Text>
                  <HStack>
                    <Code p={2} borderRadius="md" fontSize="xs" wordBreak="break-all" flex={1}>
                      {selected.copy_value.value}
                    </Code>
                    <Button
                      size="xs"
                      onClick={() => {
                        navigator.clipboard?.writeText(selected.copy_value!.value);
                        toast({ title: 'Copiado', status: 'success', duration: 1200, isClosable: true });
                      }}
                    >
                      Copiar
                    </Button>
                  </HStack>
                </Box>
              )}

              {selected.note && (
                <Alert status="info" borderRadius="md" alignItems="flex-start">
                  <AlertIcon mt={1} />
                  <Box fontSize="xs" color="gray.700" whiteSpace="pre-line">
                    {selected.note}
                  </Box>
                </Alert>
              )}

              <FormControl isRequired>
                <FormLabel fontSize="sm">Nome da integração</FormLabel>
                <Input value={name} onChange={(e) => setName(e.target.value)} size="sm" placeholder="Ex.: CRM interno" />
              </FormControl>

              <FormControl isRequired={!isEdit}>
                <FormLabel fontSize="sm">Base de conhecimento (destino)</FormLabel>
                {isEdit ? (
                  <Input value={targetCollection} size="sm" isDisabled />
                ) : creatingCollection ? (
                  <HStack>
                    <Input
                      size="sm"
                      autoFocus
                      placeholder="Nome da nova base (ex.: pedidos)"
                      value={targetCollection}
                      onChange={(e) => setTargetCollection(e.target.value)}
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => { setCreatingCollection(false); setTargetCollection(''); }}
                    >
                      Cancelar
                    </Button>
                  </HStack>
                ) : (
                  <Select
                    size="sm"
                    placeholder="Selecione uma base"
                    value={targetCollection}
                    onChange={(e) => {
                      if (e.target.value === '__new__') { setCreatingCollection(true); setTargetCollection(''); }
                      else setTargetCollection(e.target.value);
                    }}
                  >
                    {collections.map((c) => (
                      <option key={c.name} value={c.name}>{c.name}</option>
                    ))}
                    <option value="__new__">➕ Criar nova base...</option>
                  </Select>
                )}
                <FormHelperText fontSize="xs">
                  {isEdit
                    ? 'A base destino não pode ser alterada na edição.'
                    : 'Onde os dados ficam indexados (usados no chat e nas automações).'}
                </FormHelperText>
              </FormControl>

              {/* Regra de sincronização (F1): quando ingerir. Tempo-real/push não configura. */}
              {selected.mode === 'agent' ? (
                <FormControl>
                  <FormLabel fontSize="sm">Sincronização</FormLabel>
                  <Text fontSize="sm" color="gray.600">
                    Tempo real — o Agente envia os dados assim que aparecem. Sem frequência a configurar.
                  </Text>
                </FormControl>
              ) : (
                <FormControl>
                  <FormLabel fontSize="sm">Quando sincronizar</FormLabel>
                  <Select size="sm" value={syncMode} onChange={(e) => setSyncMode(e.target.value)}>
                    <option value="periodic">Periodicamente (automático)</option>
                    <option value="manual">Só quando eu mandar (manual)</option>
                  </Select>
                  {syncMode === 'periodic' && (
                    <Select size="sm" mt={2} value={syncInterval} onChange={(e) => setSyncInterval(Number(e.target.value))}>
                      <option value={15}>A cada 15 minutos</option>
                      <option value={60}>A cada 1 hora</option>
                      <option value={360}>A cada 6 horas</option>
                      <option value={1440}>Uma vez por dia</option>
                    </Select>
                  )}
                  <FormHelperText fontSize="xs">
                    {syncMode === 'manual'
                      ? 'Só roda quando você clicar em "Rodar agora". Nada acontece sozinho.'
                      : 'Só os itens novos/alterados são processados (ingestão incremental) — não reprocessa tudo.'}
                  </FormHelperText>
                </FormControl>
              )}

              <Divider />

              {selected.fields.map((field) => (
                <FormControl key={field.name} isRequired={field.required}>
                  <FormLabel fontSize="sm">{field.label}</FormLabel>
                  {field.table_picker ? (
                    tables.length > 0 ? (
                      <Select
                        size="sm"
                        placeholder="Selecione a tabela"
                        value={config[field.name] ?? ''}
                        onChange={(e) => onTablePick(field.name, e.target.value)}
                      >
                        {tables.map((t) => <option key={t} value={t}>{t}</option>)}
                      </Select>
                    ) : (
                      <Input
                        size="sm"
                        placeholder="Conecte para listar as tabelas"
                        value={config[field.name] ?? ''}
                        onChange={(e) => setField(field.name, e.target.value)}
                      />
                    )
                  ) : (
                    <FieldInput
                      field={field}
                      value={config[field.name]}
                      onChange={(v) => setField(field.name, v)}
                      columns={columns}
                    />
                  )}
                  {field.table_picker && tables.length === 0 && (
                    <FormHelperText fontSize="xs" color="orange.500">Clique em "Testar conexão" para listar as tabelas.</FormHelperText>
                  )}
                  {field.column_picker && columns.length === 0 && (
                    <FormHelperText fontSize="xs" color="orange.500">Selecione a tabela para listar as colunas.</FormHelperText>
                  )}
                  {isEdit && field.secret && (
                    <FormHelperText fontSize="xs">Deixe em branco para manter o valor atual.</FormHelperText>
                  )}
                  {field.help && <FormHelperText fontSize="xs">{field.help}</FormHelperText>}
                </FormControl>
              ))}

              {selected.mode === 'agent' && (
                <Alert status="info" borderRadius="md" fontSize="sm">
                  <AlertIcon />
                  Ao salvar, você recebe o token e o guia para instalar o Agente na sua rede.
                </Alert>
              )}

              {selected.mode !== 'form' && selected.mode !== 'agent' && (
                <Alert status="info" borderRadius="md" fontSize="sm">
                  <AlertIcon />
                  {selected.mode === 'whatsapp'
                    ? 'Depois de salvar, clique em "Conectar" no cartão para gerar o QR Code e escanear com o celular.'
                    : 'Depois de salvar, clique em "Conectar" no cartão da integração para autorizar o acesso.'}
                </Alert>
              )}

              {testResult && (
                <Alert status={testResult.ok ? 'success' : 'error'} borderRadius="md" fontSize="sm">
                  <AlertIcon />
                  {testResult.message}
                </Alert>
              )}
            </VStack>
          )}
        </ModalBody>

        <ModalFooter>
          {selected && (
            <HStack spacing={3} width="100%" justify="space-between">
              {isEdit ? (
                <Box />
              ) : (
                <Button variant="ghost" size="sm" onClick={() => setSelected(null)}>
                  Voltar
                </Button>
              )}
              <HStack>
                {/* Só conectores de formulário testam antes de salvar; oauth/whatsapp
                    conectam depois (autorização/QR no cartão da integração). */}
                {selected.mode === 'form' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => runTest()}
                    isLoading={testing}
                    loadingText="Testando"
                    isDisabled={missingForTest()}
                  >
                    Testar conexão
                  </Button>
                )}
                <Button
                  colorScheme="brand"
                  size="sm"
                  onClick={save}
                  isLoading={saving}
                  isDisabled={missingRequired() || (selected.mode === 'form' && !testResult?.ok && !isEdit)}
                  leftIcon={<CheckCircleIcon />}
                >
                  {isEdit ? 'Salvar alterações' : 'Salvar'}
                </Button>
              </HStack>
            </HStack>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// Modal de conexão do WhatsApp: gera o QR, faz poll do estado e, ao conectar,
// lista as conversas para o usuário escolher quais ingerir.
const WhatsAppConnectModal = ({
  integrationId,
  isOpen,
  onClose,
  onDone,
}: {
  integrationId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onDone: () => void;
}) => {
  const toast = useToast();
  const [qr, setQr] = useState<string | null>(null);
  const [state, setState] = useState<string>('');
  const [chats, setChats] = useState<Array<{ id: string; name: string }>>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !integrationId) return;
    let active = true;
    let interval: any;
    setQr(null);
    setState('');
    setChats([]);
    setSelected(new Set());
    setLabels({});

    (async () => {
      try {
        const { qrcode } = await integrationsApi.whatsappConnect(integrationId);
        if (!active) return;
        setQr(qrcode);
        interval = setInterval(async () => {
          try {
            const res = await integrationsApi.whatsappState(integrationId);
            if (!active) return;
            setState(res.state);
            if (res.state === 'open') {
              clearInterval(interval);
              setQr(null);
              const c = await integrationsApi.whatsappChats(integrationId);
              if (active) setChats(c.chats);
            }
          } catch {
            /* mantém o poll */
          }
        }, 3000);
      } catch (e: any) {
        if (active) toast({ title: 'Falha ao iniciar conexão', status: 'error', duration: 4000, isClosable: true });
      }
    })();

    return () => {
      active = false;
      if (interval) clearInterval(interval);
    };
  }, [isOpen, integrationId, toast]);

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const save = async () => {
    if (!integrationId) return;
    setSaving(true);
    try {
      // Só envia rótulos das conversas selecionadas.
      const sel = Array.from(selected);
      const usedLabels: Record<string, string> = {};
      sel.forEach((id) => {
        if (labels[id]?.trim()) usedLabels[id] = labels[id].trim();
      });
      await integrationsApi.whatsappSaveChats(integrationId, sel, usedLabels);
      toast({ title: 'Conversas salvas', status: 'success', duration: 2500, isClosable: true });
      onDone();
      onClose();
    } catch {
      toast({ title: 'Erro ao salvar conversas', status: 'error', duration: 3000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  const qrSrc = qr ? (qr.startsWith('data:') ? qr : `data:image/png;base64,${qr}`) : null;
  const connected = state === 'open';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Conectar WhatsApp</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {!connected ? (
            <VStack spacing={4} py={2}>
              <Text fontSize="sm" color="gray.600" textAlign="center">
                No celular: WhatsApp → <b>Aparelhos conectados</b> → <b>Conectar um aparelho</b> e escaneie:
              </Text>
              {qrSrc ? (
                <Image src={qrSrc} alt="QR Code do WhatsApp" boxSize="240px" />
              ) : (
                <Spinner color="brand.500" />
              )}
              <Text fontSize="xs" color="gray.400">
                Aguardando conexão{state ? ` (${state})` : ''}…
              </Text>
            </VStack>
          ) : (
            <VStack align="stretch" spacing={2}>
              <Alert status="success" borderRadius="md" fontSize="sm">
                <AlertIcon />
                Conectado! Escolha as conversas a ingerir:
              </Alert>
              {chats.length === 0 && <Text fontSize="sm" color="gray.500">Nenhuma conversa encontrada ainda.</Text>}
              {chats.map((c) => (
                <Box key={c.id}>
                  <Checkbox isChecked={selected.has(c.id)} onChange={() => toggle(c.id)} colorScheme="brand">
                    <Text fontSize="sm">{c.name}</Text>
                  </Checkbox>
                  {selected.has(c.id) && (
                    <Input
                      mt={1}
                      ml={6}
                      size="xs"
                      placeholder="Apelido (opcional, ex.: Motiva) — vira parte da busca"
                      value={labels[c.id] || ''}
                      onChange={(e) => setLabels((p) => ({ ...p, [c.id]: e.target.value }))}
                    />
                  )}
                </Box>
              ))}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          {connected && (
            <Button colorScheme="brand" size="sm" onClick={save} isLoading={saving} isDisabled={selected.size === 0}>
              Salvar conversas ({selected.size})
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// Guia de instalação do Agente Local: token (mostrado uma vez), passos, .env e comando.
const AgentGuideModal = ({
  isOpen,
  onClose,
  integrationId,
  initialGuide,
  initialToken,
}: {
  isOpen: boolean;
  onClose: () => void;
  integrationId: string | null;
  initialGuide: AgentGuide | null;
  initialToken: string | null;
}) => {
  const toast = useToast();
  const [guide, setGuide] = useState<AgentGuide | null>(initialGuide);
  const [token, setToken] = useState<string | null>(initialToken);
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    setGuide(initialGuide);
    setToken(initialToken);
  }, [initialGuide, initialToken, isOpen]);

  // Aberto pelo cartão (sem guia em mãos) → busca o guia (sem token).
  useEffect(() => {
    if (!isOpen || initialGuide || !integrationId) return;
    integrationsApi.agentGuide(integrationId).then((r) => setGuide(r.guide)).catch(() => {});
  }, [isOpen, integrationId, initialGuide]);

  const copy = (text: string, label: string) => {
    navigator.clipboard?.writeText(text);
    toast({ title: `${label} copiado`, status: 'success', duration: 1500, isClosable: true });
  };

  const regenerate = async () => {
    if (!integrationId) return;
    setRegenerating(true);
    try {
      const r = await integrationsApi.regenerateAgentToken(integrationId);
      setToken(r.agent_token);
      setGuide(r.agent_guide);
      toast({ title: 'Novo token gerado', description: 'O token anterior deixou de funcionar.', status: 'success', duration: 2500, isClosable: true });
    } catch {
      toast({ title: 'Falha ao gerar token', status: 'error', duration: 3000, isClosable: true });
    } finally {
      setRegenerating(false);
    }
  };

  const envText = guide ? (token ? guide.env_example.replace('<COLE_AQUI_O_SEU_TOKEN>', token) : guide.env_example) : '';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Agente Local — instalação (self-service)</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {!guide ? (
            <Flex justify="center" py={10}><Spinner color="brand.500" /></Flex>
          ) : (
            <VStack align="stretch" spacing={4}>
              {token ? (
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={1}>
                    Seu token — copie agora (não será mostrado de novo):
                  </Text>
                  <HStack>
                    <Code p={2} borderRadius="md" fontSize="xs" wordBreak="break-all" flex={1}>{token}</Code>
                    <Button size="sm" onClick={() => copy(token, 'Token')}>Copiar</Button>
                  </HStack>
                </Box>
              ) : (
                <Alert status="warning" borderRadius="md" fontSize="sm">
                  <AlertIcon />
                  O token não é exibido depois de criado. Se você não o guardou, gere um novo abaixo.
                </Alert>
              )}

              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={1}>Passo a passo</Text>
                <OrderedList fontSize="sm" color="gray.600" spacing={1}>
                  {guide.steps.map((s, i) => <ListItem key={i}>{s}</ListItem>)}
                </OrderedList>
              </Box>

              <Box>
                <HStack justify="space-between" mb={1}>
                  <Text fontSize="sm" fontWeight="medium">Arquivo .env (cole no pacote do Agente)</Text>
                  <Button size="xs" onClick={() => copy(envText, '.env')}>Copiar .env</Button>
                </HStack>
                <Textarea value={envText} readOnly fontFamily="mono" fontSize="xs" rows={14} />
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={1}>Subir o Agente</Text>
                <Code p={2} borderRadius="md" fontSize="xs">{guide.run_command}</Code>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Envia para: {guide.api_url}
                </Text>
              </Box>

              {integrationId && (
                <Button size="sm" variant="outline" onClick={regenerate} isLoading={regenerating} alignSelf="flex-start">
                  Gerar novo token
                </Button>
              )}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>Fechar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// Garante que o Google Picker fique ACIMA do nosso modal (z-index do Chakra ~1400).
const ensurePickerOnTop = () => {
  if (document.getElementById('picker-zfix')) return;
  const st = document.createElement('style');
  st.id = 'picker-zfix';
  st.innerHTML = '.picker-dialog-bg{z-index:20000 !important}.picker-dialog{z-index:20001 !important}';
  document.head.appendChild(st);
};

// Carrega a API do Google Picker uma única vez; resolve quando google.picker existir.
let _pickerLoading: Promise<void> | null = null;
const loadGooglePicker = (): Promise<void> => {
  ensurePickerOnTop();
  const w = window as any;
  if (w.google?.picker) return Promise.resolve();
  if (_pickerLoading) return _pickerLoading;
  _pickerLoading = new Promise<void>((resolve, reject) => {
    const loadModule = () => w.gapi.load('picker', { callback: () => resolve() });
    if (w.gapi) { loadModule(); return; }
    const s = document.createElement('script');
    s.src = 'https://apis.google.com/js/api.js';
    s.async = true;
    s.onload = loadModule;
    s.onerror = () => { _pickerLoading = null; reject(new Error('Falha ao carregar o Google Drive')); };
    document.body.appendChild(s);
  });
  return _pickerLoading;
};

// Seletor de pasta do Google Drive (pós-conexão) via Google Picker — escopo drive.file.
const DriveScopeModal = ({
  integrationId,
  currentScope,
  isOpen,
  onClose,
  onSaved,
}: {
  integrationId: string | null;
  currentScope?: string;   // pasta atualmente selecionada (nome) — para reabrir e saber
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const toast = useToast();
  const [selected, setSelected] = useState<{ id: string; name: string } | null>(null);
  const [loading, setLoading] = useState(false);  // abrindo o Picker
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (isOpen) setSelected(null); }, [isOpen]);

  const openPicker = async () => {
    if (!integrationId) return;
    setLoading(true);
    try {
      const cfg = await integrationsApi.drivePickerConfig(integrationId);
      await loadGooglePicker();
      const g = (window as any).google;
      // Só pastas; permite selecionar a própria pasta (não só entrar nela).
      const view = new g.picker.DocsView(g.picker.ViewId.FOLDERS)
        .setSelectFolderEnabled(true)
        .setIncludeFolders(true)
        .setMimeTypes('application/vnd.google-apps.folder');
      const picker = new g.picker.PickerBuilder()
        .setAppId(cfg.app_id)
        .setOAuthToken(cfg.access_token)
        .setDeveloperKey(cfg.api_key)
        .enableFeature(g.picker.Feature.SUPPORT_DRIVES)  // inclui Drives Compartilhados
        .addView(view)
        .setCallback((data: any) => {
          const action = data[g.picker.Response.ACTION] ?? data.action;
          if (action === g.picker.Action.PICKED && data.docs?.[0]) {
            const doc = data.docs[0];
            setSelected({ id: doc.id, name: doc.name });
            toast({ title: `Pasta selecionada: ${doc.name}`, description: 'Clique em Salvar para confirmar.', status: 'success', duration: 4000, isClosable: true });
          } else if (action === g.picker.Action.CANCEL) {
            toast({ title: 'Nenhuma pasta escolhida', status: 'info', duration: 2500, isClosable: true });
          }
        })
        .build();
      picker.setVisible(true);
    } catch (e: any) {
      toast({ title: 'Não foi possível abrir o seletor', description: e?.response?.data?.detail || e?.message || 'Tente reconectar a conta Google', status: 'error', duration: 6000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (!integrationId || !selected) return;
    setSaving(true);
    try {
      await integrationsApi.setDriveScope(integrationId, { drive_id: '', folder_id: selected.id, folder_name: selected.name });
      toast({ title: 'Pasta definida', description: selected.name, status: 'success', duration: 2500, isClosable: true });
      onSaved();
      onClose();
    } catch (e: any) {
      toast({ title: 'Erro ao salvar', description: e?.response?.data?.detail || 'Tente novamente', status: 'error', duration: 4000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered trapFocus={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Escolha a pasta do Google Drive</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <Text fontSize="sm" color="gray.600">
              Selecione no Google Drive a pasta que será sincronizada. O sistema acessa
              <b> só a pasta escolhida</b> (e o conteúdo dela) — nunca o seu Drive inteiro.
            </Text>
            {currentScope && !selected && (
              <HStack p={3} borderWidth="1px" borderRadius="md" bg="gray.50" fontSize="sm">
                <FiFolder />
                <Text>Pasta atual: <b>{currentScope}</b></Text>
              </HStack>
            )}
            <Button
              onClick={openPicker}
              isLoading={loading}
              loadingText="Abrindo o Google Drive..."
              leftIcon={<FiFolder />}
              variant="outline"
              colorScheme="brand"
            >
              {currentScope || selected ? 'Trocar pasta' : 'Escolher pasta no Google Drive'}
            </Button>
            {selected && (
              <HStack p={3} borderWidth="1px" borderRadius="md" bg="brand.50">
                <FiFolder />
                <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>{selected.name}</Text>
              </HStack>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" size="sm" mr={2} onClick={onClose}>Cancelar</Button>
          <Button
            colorScheme="brand"
            size="sm"
            onClick={save}
            isLoading={saving}
            leftIcon={<CheckCircleIcon />}
            isDisabled={!selected}
          >
            Salvar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const Integrations = () => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [editId, setEditId] = useState<string | null>(null); // id em edição (null = criar)
  const [wppId, setWppId] = useState<string | null>(null);
  const [runningId, setRunningId] = useState<string | null>(null);
  // Modal do Agente Local: { integrationId, guide, token }.
  const [agentModal, setAgentModal] = useState<{ integrationId: string | null; guide: AgentGuide | null; token: string | null } | null>(null);
  const [driveScopeId, setDriveScopeId] = useState<string | null>(null); // seletor de pasta do Drive

  const openCreate = () => { setEditId(null); onOpen(); };
  const openEdit = (it: Integration) => { setEditId(it.id); onOpen(); };
  const closeWizard = () => { setEditId(null); onClose(); };
  // Histórico de execuções (logs).
  const [historyRuns, setHistoryRuns] = useState<Array<{ status: string; indexed: number; detail: string; created_at: string | null }> | null>(null);
  const [historyName, setHistoryName] = useState('');
  const openHistory = async (it: Integration) => {
    setHistoryName(it.name);
    setHistoryRuns([]);
    try {
      setHistoryRuns(await integrationsApi.runs(it.id));
    } catch {
      setHistoryRuns([]);
    }
  };

  const { data: types = [] } = useQuery('integration-types', integrationsApi.getTypes, {
    staleTime: Infinity,
  });
  const { data: integrations = [], refetch, isLoading } = useQuery(
    'integrations',
    integrationsApi.list,
  );

  const oauthTypes = new Set(types.filter((t) => t.mode === 'oauth').map((t) => t.type));
  const whatsappTypes = new Set(types.filter((t) => t.mode === 'whatsapp').map((t) => t.type));
  const agentTypes = new Set(types.filter((t) => t.mode === 'agent').map((t) => t.type));
  const isWhatsapp = (it: Integration) => whatsappTypes.has(it.type);
  const isAgent = (it: Integration) => agentTypes.has(it.type);
  // Google Drive conectado → permite escolher Drive Compartilhado + pasta.
  const isGoogleDriveConnected = (it: Integration) =>
    (it.type === 'google_drive' || it.type === 'google_drive_managed') &&
    (it.status === 'connected' || it.status === 'ok');
  // Fonte de arquivos ampla (Drive/S3/SharePoint) conectada, periódica e SEM pasta:
  // não sincroniza sozinha (freio de capacidade). Avisar o usuário.
  const needsScope = (it: Integration) =>
    ['google_drive', 'google_drive_managed', 's3', 'sharepoint', 'sharepoint_managed'].includes(it.type) &&
    (it.status === 'connected' || it.status === 'ok') &&
    (it.sync_mode ?? 'periodic') === 'periodic' &&
    it.scoped === false;
  // Editável: conectores de formulário (ex.: SQL/S3). OAuth/WhatsApp/Agente têm
  // fluxo próprio — editar config crua não se aplica.
  const isFormType = (it: Integration) =>
    !whatsappTypes.has(it.type) && !oauthTypes.has(it.type) && !agentTypes.has(it.type);
  const needsConnect = (it: Integration) =>
    oauthTypes.has(it.type) && it.status !== 'connected' && it.status !== 'ok';

  const connect = async (it: Integration) => {
    try {
      const { authorize_url } = await integrationsApi.startOAuth(it.id);
      window.open(authorize_url, '_blank', 'width=620,height=760');
      toast({
        title: 'Autorize na nova aba',
        description: 'Assim que você autorizar, o status atualiza sozinho aqui.',
        status: 'info',
        duration: 6000,
        isClosable: true,
      });
      // Atualiza o cartão automaticamente quando a autorização concluir (sem
      // precisar clicar em "Atualizar"). Faz poll por ~2 min e para ao conectar.
      let tries = 0;
      const timer = setInterval(async () => {
        tries += 1;
        try {
          const list = await integrationsApi.list();
          const cur = list.find((x) => x.id === it.id);
          if (cur && (cur.status === 'connected' || cur.status === 'ok')) {
            clearInterval(timer);
            refetch();
            toast({ title: 'Conectado com sucesso', status: 'success', duration: 3000, isClosable: true });
          }
        } catch {
          /* ignora e segue tentando */
        }
        if (tries >= 40) clearInterval(timer);
      }, 3000);
    } catch (e: any) {
      toast({
        title: 'Não foi possível iniciar a conexão',
        description: e?.response?.data?.detail || 'Verifique a configuração',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const runNow = async (it: Integration) => {
    setRunningId(it.id);
    try {
      const res = await integrationsApi.run(it.id);
      if (res.queued) {
        // Sync foi para a fila (worker) — não bloqueia; resultado vai ao histórico.
        toast({
          title: 'Sincronização iniciada',
          description: 'Estamos buscando os dados em segundo plano. O resultado aparece no histórico em instantes.',
          status: 'info',
          duration: 5000,
          isClosable: true,
        });
      } else {
        const r = res.result || {};
        toast({
          title: 'Ingestão concluída',
          description: `${r.indexed ?? 0} documento(s) indexado(s) de ${r.rows ?? 0} registro(s).`,
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
      }
      refetch();
    } catch (e: any) {
      toast({
        title: 'Falha na ingestão',
        description: e?.response?.data?.detail || 'Verifique a conexão',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      refetch();
    } finally {
      setRunningId(null);
    }
  };

  const remove = async (it: Integration) => {
    if (!window.confirm(`Excluir a integração "${it.name}"?`)) return;
    try {
      await integrationsApi.remove(it.id);
      toast({ title: 'Integração excluída', status: 'info', duration: 2000, isClosable: true });
      refetch();
    } catch {
      toast({ title: 'Erro ao excluir', status: 'error', duration: 3000, isClosable: true });
    }
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={1}>
        <Heading size="lg" color="brand.700">
          Integrações
        </Heading>
        <HStack>
          <Button variant="ghost" size="sm" onClick={() => refetch()}>
            Atualizar
          </Button>
          <Button colorScheme="brand" leftIcon={<AddIcon />} onClick={openCreate} size="sm">
            Nova integração
          </Button>
        </HStack>
      </Flex>
      <Text color="gray.600" mb={6}>
        Conecte fontes de dados (banco interno, S3, e em breve Google/SharePoint) para ingestão
        automática no RAG. As credenciais ficam criptografadas.
      </Text>

      {isLoading && (
        <Flex justify="center" py={16}>
          <Spinner color="brand.500" />
        </Flex>
      )}

      {!isLoading && integrations.length === 0 && (
        <Box borderWidth="1px" borderRadius="lg" borderStyle="dashed" py={12} px={6} textAlign="center">
          <VStack spacing={4}>
            <HStack spacing={3}>
              {['google_drive', 'sharepoint', 's3', 'sql', 'whatsapp'].map((t) => (
                <ConnectorIcon key={t} type={t} size="34px" />
              ))}
            </HStack>
            <Box>
              <Heading size="md" color="gray.700">Conecte sua primeira fonte</Heading>
              <Text color="gray.500" mt={1} maxW="lg" mx="auto">
                Banco interno, Google Drive, SharePoint, S3 ou WhatsApp. Os dados entram
                automaticamente na base e ficam prontos para o chat.
              </Text>
            </Box>
            <Button colorScheme="brand" leftIcon={<AddIcon />} onClick={openCreate}>
              Nova integração
            </Button>
          </VStack>
        </Box>
      )}

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        {integrations.map((it) => (
          <Box key={it.id} borderWidth="1px" borderRadius="lg" p={4} bg="white" shadow="sm">
            <Flex justify="space-between" align="flex-start">
              <HStack align="flex-start" spacing={3}>
                <ConnectorBadge type={it.type} size="40px" />
                <VStack align="flex-start" spacing={1}>
                  <Text fontWeight="semibold">{it.name}</Text>
                  <HStack fontSize="sm" color="gray.500">
                    <Text>→ {it.target_collection}</Text>
                  </HStack>
                  {it.scope_label && (
                    <HStack fontSize="xs" color="gray.500" spacing={1}>
                      <FiFolder size={12} />
                      <Text>Pasta: <b>{it.scope_label}</b></Text>
                    </HStack>
                  )}
                </VStack>
              </HStack>
              <Badge colorScheme={statusColor(it.status)}>{statusLabel(it.status)}</Badge>
            </Flex>

            <Text fontSize="xs" color="gray.400" mt={2}>
              Último run: {formatDate(it.last_run)}
              {it.last_result?.indexed != null && ` · ${it.last_result.indexed} indexado(s)`}
            </Text>

            {/* Erro amigável + ação (ex.: "Ativar a Google Drive API"). */}
            {it.status === 'error' && it.last_result?.error && (
              <Alert status="warning" borderRadius="md" mt={2} fontSize="xs" py={2}>
                <AlertIcon />
                <Box>
                  <Text>{it.last_result.error}</Text>
                  {it.last_result.action_url && (
                    <Button
                      as="a"
                      href={it.last_result.action_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="xs"
                      mt={1}
                      colorScheme="orange"
                      variant="outline"
                      leftIcon={<FiExternalLink />}
                    >
                      {it.last_result.action_label || 'Resolver'}
                    </Button>
                  )}
                </Box>
              </Alert>
            )}

            {/* Freio de capacidade: fonte ampla sem pasta não sincroniza sozinha. */}
            {needsScope(it) && (
              <Alert status="info" borderRadius="md" mt={2} fontSize="xs" py={2}>
                <AlertIcon />
                <Box>
                  <Text>
                    Defina uma pasta para sincronizar automaticamente — sem isso,
                    não puxamos a fonte inteira (evita lentidão e custo). Você ainda
                    pode rodar manualmente.
                  </Text>
                  {isGoogleDriveConnected(it) && (
                    <Button size="xs" mt={1} colorScheme="teal" variant="outline"
                      leftIcon={<FiDatabase />} onClick={() => setDriveScopeId(it.id)}>
                      Escolher pasta
                    </Button>
                  )}
                </Box>
              </Alert>
            )}

            <HStack mt={3} spacing={2}>
              {isWhatsapp(it) ? (
                <>
                  <Button size="xs" leftIcon={<FiLink />} colorScheme="green" onClick={() => setWppId(it.id)}>
                    {it.status === 'connected' ? 'Conversas' : 'Conectar'}
                  </Button>
                  {it.status === 'connected' && (
                    <Button
                      size="xs"
                      leftIcon={<FiPlay />}
                      colorScheme="brand"
                      variant="outline"
                      onClick={() => runNow(it)}
                      isLoading={runningId === it.id}
                      loadingText="Rodando"
                    >
                      Rodar agora
                    </Button>
                  )}
                </>
              ) : isAgent(it) ? (
                <Button
                  size="xs"
                  leftIcon={<FiLink />}
                  colorScheme="teal"
                  onClick={() => setAgentModal({ integrationId: it.id, guide: null, token: null })}
                >
                  Agente / Instalação
                </Button>
              ) : needsConnect(it) ? (
                <Button
                  size="xs"
                  leftIcon={<FiLink />}
                  colorScheme="purple"
                  onClick={() => connect(it)}
                >
                  Conectar
                </Button>
              ) : (
                <Button
                  size="xs"
                  leftIcon={<FiPlay />}
                  colorScheme="brand"
                  variant="outline"
                  onClick={() => runNow(it)}
                  isLoading={runningId === it.id}
                  loadingText="Rodando"
                >
                  Rodar agora
                </Button>
              )}
              <Button size="xs" leftIcon={<FiClock />} variant="ghost" onClick={() => openHistory(it)}>
                Histórico
              </Button>
              {isGoogleDriveConnected(it) && (
                <Button size="xs" leftIcon={<FiDatabase />} variant="ghost" colorScheme="teal" onClick={() => setDriveScopeId(it.id)}>
                  Escolher pasta
                </Button>
              )}
              {isFormType(it) && (
                <IconButton
                  aria-label="Editar"
                  icon={<EditIcon />}
                  size="xs"
                  variant="ghost"
                  onClick={() => openEdit(it)}
                />
              )}
              <IconButton
                aria-label="Excluir"
                icon={<DeleteIcon />}
                size="xs"
                variant="ghost"
                colorScheme="red"
                onClick={() => remove(it)}
              />
            </HStack>
          </Box>
        ))}
      </SimpleGrid>

      {/* Histórico de execuções (logs) */}
      <Modal isOpen={historyRuns !== null} onClose={() => setHistoryRuns(null)} size="lg" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Execuções — {historyName}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {!historyRuns || historyRuns.length === 0 ? (
              <Text color="gray.500" fontSize="sm">Ainda sem execuções registradas.</Text>
            ) : (
              <VStack align="stretch" spacing={2}>
                {historyRuns.map((r, i) => (
                  <HStack key={i} justify="space-between" borderWidth="1px" borderRadius="md" p={2} fontSize="sm">
                    <HStack>
                      <Badge colorScheme={r.status === 'ok' ? 'green' : 'red'}>{r.status}</Badge>
                      <Text>{r.indexed} indexado(s)</Text>
                      {r.detail && <Text color="red.500" noOfLines={1}>{r.detail}</Text>}
                    </HStack>
                    <Text fontSize="xs" color="gray.400">
                      {r.created_at ? new Date(r.created_at).toLocaleString('pt-BR') : ''}
                    </Text>
                  </HStack>
                ))}
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      <IntegrationWizard
        types={types}
        isOpen={isOpen}
        onClose={closeWizard}
        editId={editId}
        onSaved={refetch}
        onAgentCreated={(token, guide) => setAgentModal({ integrationId: null, guide, token })}
      />
      <WhatsAppConnectModal
        integrationId={wppId}
        isOpen={!!wppId}
        onClose={() => setWppId(null)}
        onDone={refetch}
      />
      <AgentGuideModal
        isOpen={!!agentModal}
        onClose={() => setAgentModal(null)}
        integrationId={agentModal?.integrationId ?? null}
        initialGuide={agentModal?.guide ?? null}
        initialToken={agentModal?.token ?? null}
      />
      <DriveScopeModal
        isOpen={!!driveScopeId}
        integrationId={driveScopeId}
        currentScope={integrations.find((i) => i.id === driveScopeId)?.scope_label || ''}
        onClose={() => setDriveScopeId(null)}
        onSaved={refetch}
      />
    </Box>
  );
};

export default Integrations;
